import React, { useEffect, useState, useMemo } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { BAILLEUR_LINKS } from './links';
import { bailleurApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TID } from '@/constants/testIds';

const FR_MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS = ['Lu','Ma','Me','Je','Ve','Sa','Di'];

// Priority order — worse-first — used to pick the dominant status of a day
const STATUS_PRIORITY = { late: 4, partial: 3, pending: 2, paid: 1 };

const STATUS_CELL = {
  paid:    { bg: 'bg-emerald-50 border-emerald-300', dot: 'bg-emerald-500', text: 'text-emerald-800', label: 'Payé' },
  partial: { bg: 'bg-amber-50 border-amber-300',     dot: 'bg-amber-500',   text: 'text-amber-800',   label: 'Partiel' },
  late:    { bg: 'bg-red-50 border-red-300',         dot: 'bg-red-500',     text: 'text-red-800',     label: 'En retard' },
  pending: { bg: 'bg-sky-50 border-sky-300',         dot: 'bg-sky-500',     text: 'text-sky-800',     label: 'À venir' },
};

function initialsOf(loc) {
  if (!loc) return '?';
  const p = (loc.prenom || '').trim();
  const n = (loc.nom || '').trim();
  return `${p[0] || ''}${n[0] || ''}`.toUpperCase() || '?';
}

function dominantStatus(evs) {
  let winner = null;
  let best = -1;
  for (const e of evs) {
    const p = STATUS_PRIORITY[e.statut] ?? 0;
    if (p > best) { best = p; winner = e.statut; }
  }
  return winner;
}

export default function BailleurCalendrier() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await bailleurApi.calendar(month, year);
        setEvents(data);
      } finally { setLoading(false); }
    })();
  }, [month, year]);

  const days = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0).getDate();
    const startWeekday = (first.getDay() + 6) % 7; // Monday-first
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= last; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const d = new Date(e.date_echeance);
      const day = d.getDate();
      (map[day] = map[day] || []).push(e);
    }
    return map;
  }, [events]);

  const today = new Date();
  const isCurrentMonth = today.getMonth() + 1 === month && today.getFullYear() === year;
  const todayDay = today.getDate();

  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };

  // Global summary of the month
  const summary = useMemo(() => {
    const s = { paid: 0, partial: 0, late: 0, pending: 0 };
    for (const e of events) if (s[e.statut] !== undefined) s[e.statut] += 1;
    return s;
  }, [events]);

  return (
    <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title="Calendrier des échéances">
      <div className="lb-card p-6" data-testid="bailleur-calendar">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Vue mensuelle</div>
            <div className="text-2xl font-bold text-[#0D1B2A] mt-0.5">{FR_MONTHS[month - 1]} {year}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="cal-prev" variant="outline" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
            <Button data-testid="cal-today" variant="outline" onClick={() => { const n = new Date(); setMonth(n.getMonth() + 1); setYear(n.getFullYear()); }}>Aujourd&apos;hui</Button>
            <Button data-testid="cal-next" variant="outline" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Month summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">Payées</div>
            <div className="text-xl font-bold text-emerald-800">{summary.paid}</div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-700">Partielles</div>
            <div className="text-xl font-bold text-amber-800">{summary.partial}</div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-red-700">En retard</div>
            <div className="text-xl font-bold text-red-800">{summary.late}</div>
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-700">À venir</div>
            <div className="text-xl font-bold text-sky-800">{summary.pending}</div>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {DAYS.map((d) => <div key={d} className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 text-center">{d}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {days.map((d, i) => {
            const evs = d ? (eventsByDay[d] || []) : [];
            const status = evs.length ? dominantStatus(evs) : null;
            const style = status ? STATUS_CELL[status] : null;
            const isToday = d && isCurrentMonth && d === todayDay;

            const baseCell = d
              ? (style ? `${style.bg} border` : 'bg-white border border-slate-200')
              : 'bg-transparent border border-transparent';

            return (
              <div
                key={i}
                data-testid={d ? `cal-day-${d}` : undefined}
                data-status={status || undefined}
                className={`min-h-[104px] rounded-lg p-2 flex flex-col transition-shadow ${baseCell} ${isToday ? 'ring-2 ring-[#1677FF] ring-offset-1' : ''}`}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className={`text-xs font-bold ${style ? style.text : 'text-[#0D1B2A]'}`}>{d}</div>
                      {status && <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} title={style.label} />}
                    </div>
                    <div className="mt-1 space-y-1 flex-1">
                      {evs.slice(0, 2).map((e) => (
                        <div key={e.id} className="flex items-center gap-1.5 text-[10px]" title={`${e.locataire?.prenom || ''} ${e.locataire?.nom || ''} — ${STATUS_CELL[e.statut]?.label || e.statut}`}>
                          <span className={`shrink-0 w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center text-white ${STATUS_CELL[e.statut]?.dot || 'bg-slate-400'}`}>
                            {initialsOf(e.locataire)}
                          </span>
                          <span className="truncate text-slate-700">{e.locataire?.prenom} {e.locataire?.nom?.[0]}.</span>
                        </div>
                      ))}
                      {evs.length > 2 && (
                        <div className={`text-[10px] font-semibold ${style ? style.text : 'text-slate-500'}`}>+{evs.length - 2} autre(s)</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-emerald-300 bg-emerald-50" /> Payé</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-amber-300 bg-amber-50" /> Partiel</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-red-300 bg-red-50" /> En retard</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-sky-300 bg-sky-50" /> À venir</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm ring-2 ring-[#1677FF]" /> Aujourd&apos;hui</div>
        </div>
      </div>
    </AppLayout>
  );
}
